import React, { useState, useEffect, useRef } from 'react';
import { BACKEND_URL } from '../config';

// Common emojis for quick access
const EMOJI_LIST = ['😀', '😊', '👍', '👏', '💪', '🎉', '❤️', '🔥', '✅', '⭐', '🙏', '💯', '🏃', '🥗', '🍎', '💧', '😴', '🎯', '📈', '💬'];

function normalizeContact(raw) {
  const c = raw?.auth_info || raw?.user || raw || {};
  const user_id = c.user_id || c.id || c._id || raw?.user_id || raw?.id;
  if (!user_id) return null;

  return {
    user_id,
    name: c.name || raw?.name || c.email || raw?.email || user_id,
    email: c.email || raw?.email || '',
    role: c.role || raw?.role || (c.is_admin ? 'admin' : c.is_trainer ? 'trainer' : 'user'),
  };
}

function MessagesView({
  messages,
  userId,
  isAdmin,
  onSendMessage,
  onMarkRead,
  token,
  onRefreshMessages,
  adminUsers = [],
  showToast
}) {
  const [view, setView] = useState('threads'); // 'threads', 'compose', 'thread'
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [recipient, setRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [messageText, setMessageText] = useState('');
  const [replyText, setReplyText] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showReplyEmoji, setShowReplyEmoji] = useState(false);
  const [showComposeEmoji, setShowComposeEmoji] = useState(false);
  const replyTextareaRef = useRef(null);
  const composeTextareaRef = useRef(null);

  const insertEmoji = (emoji, target) => {
    if (target === 'reply') {
      setReplyText(prev => prev + emoji);
      setShowReplyEmoji(false);
      replyTextareaRef.current?.focus();
    } else {
      setMessageText(prev => prev + emoji);
      setShowComposeEmoji(false);
      composeTextareaRef.current?.focus();
    }
  };

  const applyThreadReadOptimistic = (threadId) => {
    setThreads(prev =>
      prev.map(t => (t.thread_id === threadId ? { ...t, unread_count: 0 } : t))
    );
  };

  const loadContacts = async () => {
    if (!token) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/messages/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      let list = [];

      if (response.ok) {
        const data = await response.json();
        list = (data.contacts || []).map(normalizeContact).filter(Boolean);
      }

      if (isAdmin && list.length === 0) {
        list = (adminUsers || [])
          .map(u => u?.auth_info || u?.user || u)
          .map(normalizeContact)
          .filter(Boolean);
      }

      list = list.filter(c => c.user_id && c.user_id !== userId);

      const map = new Map();
      for (const c of list) map.set(c.user_id, c);
      const unique = Array.from(map.values());

      setContacts(unique);

      if (!recipient && unique.length > 0) setRecipient(unique[0].user_id);
    } catch (error) {
      console.error('Error loading contacts:', error);
      setContacts([]);
    }
  };

  const loadThreads = async () => {
    if (!token || !userId) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/messages/${userId}/threads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setThreads(data.threads || []);
      }
    } catch (error) {
      console.error('Error loading threads:', error);
    }
  };

  // ✅ UPDATED: after loading messages, explicitly mark unread ones as read
  const loadThreadMessages = async (threadId) => {
    if (!token) return;
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/messages/thread/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const msgs = data.messages || [];
        setThreadMessages(msgs);

        // Mark any messages addressed to me as read (even if backend thread_id matching is inconsistent)
        if (typeof onMarkRead === 'function') {
          const toMark = msgs
            .filter(m => m?.to_user_id === userId && (m?.read === false || m?.read === 0 || m?.read == null))
            .map(m => m.message_id)
            .filter(Boolean);

          if (toMark.length) {
            await Promise.allSettled(toMark.map(id => onMarkRead(id)));
          }
        }
      } else {
        const txt = await response.text();
        console.error("loadThreadMessages failed:", txt);
      }
    } catch (error) {
      console.error('Error loading thread messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadContacts();
    loadThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId, isAdmin, adminUsers?.length]);

  // ✅ UPDATED: refresh unread count after marking read
  const handleThreadClick = async (thread) => {
    setSelectedThread(thread);
    setView('thread');

    applyThreadReadOptimistic(thread.thread_id);

    await loadThreadMessages(thread.thread_id);

    await loadThreads();

    if (onRefreshMessages) await onRefreshMessages();
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!recipient) return showToast?.("Choose a recipient", "error");
    if (!messageText.trim()) return showToast?.("Message can’t be empty", "error");

    await onSendMessage({
      from_user_id: userId,
      to_user_id: recipient,
      subject: subject,
      message: messageText
    });

    setView('threads');

    if (contacts.length > 0) setRecipient(contacts[0].user_id);
    else setRecipient('');

    setSubject('');
    setMessageText('');
    loadThreads();
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedThread) return;

    const otherUserId = selectedThread.other_user_id;

    setLoading(true);
    try {
      await onSendMessage({
        from_user_id: userId,
        to_user_id: otherUserId,
        subject: selectedThread.latest_message?.subject || 'Re: ' + (selectedThread.subject || ''),
        message: replyText,
        thread_id: selectedThread.thread_id
      });

      setReplyText('');
      await loadThreadMessages(selectedThread.thread_id);
      loadThreads();
      if (onRefreshMessages) await onRefreshMessages();
    } catch (error) {
      console.error('Error sending reply:', error);
    }
    setLoading(false);
  };

  // Thread View
  if (view === 'thread' && selectedThread) {
    return (
      <div className="messages-view">
        <button
          onClick={() => { setView('threads'); setSelectedThread(null); }}
          className="btn-secondary"
          style={{ marginBottom: '24px' }}
          data-testid="back-to-threads"
        >
          ← Back to Messages
        </button>

        <div style={{
          background: 'var(--bg-card)',
          borderRadius: '12px',
          border: '1px solid var(--border-light)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-light)',
            background: 'var(--bg-secondary)'
          }}>
            <h2 className="heading-3" style={{ margin: 0 }}>
              {selectedThread.latest_message?.subject || 'Conversation'}
            </h2>
            <p className="body-small" style={{ color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>
              {selectedThread.message_count} message{selectedThread.message_count !== 1 ? 's' : ''} •
              with {selectedThread.other_user_name || 'User'}
            </p>
          </div>

          <div style={{
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '16px 20px'
          }}>
            {loading ? (
              <p className="body-medium" style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>Loading...</p>
            ) : (
              threadMessages.map((msg, index) => (
                <div
                  key={msg.message_id || index}
                  style={{
                    marginBottom: '16px',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    background: msg.from_user_id === userId ? 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)' : 'var(--bg-secondary)',
                    borderLeft: msg.from_user_id === userId ? '3px solid #667eea' : '3px solid var(--border-light)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span className="body-small" style={{ fontWeight: 600 }}>
                      {msg.from_user_id === userId ? 'You' : msg.from_name}
                    </span>
                    <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      {new Date(msg.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="body-medium" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {msg.message}
                  </p>
                </div>
              ))
            )}
          </div>

          <form onSubmit={handleReply} style={{
            padding: '16px',
            borderTop: '1px solid var(--border-light)'
          }}>
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <textarea
                ref={replyTextareaRef}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply..."
                className="form-input"
                style={{
                  width: '100%',
                  minHeight: '100px',
                  fontSize: '16px',
                  resize: 'vertical',
                  paddingRight: '50px',
                  boxSizing: 'border-box'
                }}
                rows="4"
                data-testid="thread-reply-input"
              />
              <button
                type="button"
                onClick={() => setShowReplyEmoji(!showReplyEmoji)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '12px',
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
                title="Add emoji"
              >
                😊
              </button>
              {showReplyEmoji && (
                <div style={{
                  position: 'absolute',
                  right: '0',
                  top: '45px',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  padding: '8px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: '4px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 100
                }}>
                  {EMOJI_LIST.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => insertEmoji(emoji, 'reply')}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '6px',
                        borderRadius: '4px',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={!replyText.trim() || loading}
              data-testid="thread-reply-btn"
              style={{ width: '100%' }}
            >
              {loading ? 'Sending...' : 'Send Reply'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Compose View (unchanged)
  if (view === 'compose') {
    return (
      <div className="messages-view">
        <button
          onClick={() => setView('threads')}
          className="btn-secondary"
          style={{ marginBottom: '24px' }}
        >
          ← Cancel
        </button>

        <div className="form-section">
          <h3 className="heading-3">New Message</h3>
          <form onSubmit={handleSend}>
            <div className="form-group">
              <label className="body-medium">Recipient</label>
              <select
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                required
                className="form-input"
                data-testid="compose-recipient-select"
              >
                {contacts.length === 0 ? (
                  <option value="">No contacts available</option>
                ) : (
                  contacts.map(c => (
                    <option key={c.user_id} value={c.user_id}>
                      {c.name} {c.email ? `(${c.email})` : ''}{c.role ? ` — ${c.role}` : ''}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="form-group">
              <label className="body-medium">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="form-input"
                placeholder="Message subject"
                data-testid="compose-subject"
              />
            </div>

            <div className="form-group">
              <label className="body-medium">Message</label>
              <div style={{ position: 'relative' }}>
                <textarea
                  ref={composeTextareaRef}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  required
                  className="form-input"
                  rows="6"
                  placeholder="Type your message..."
                  data-testid="compose-message"
                  style={{
                    minHeight: '120px',
                    fontSize: '16px',
                    resize: 'vertical',
                    paddingRight: '50px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowComposeEmoji(!showComposeEmoji)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '12px',
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                  title="Add emoji"
                >
                  😊
                </button>
                {showComposeEmoji && (
                  <div style={{
                    position: 'absolute',
                    right: '0',
                    top: '45px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    padding: '8px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 100
                  }}>
                    {EMOJI_LIST.map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => insertEmoji(emoji, 'compose')}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '20px',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '4px',
                          transition: 'background 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button type="submit" className="btn-primary" data-testid="compose-send-btn">
              Send Message
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Threads list (unchanged)
  return (
    <div className="messages-view">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div className="card ct-page-header" style={{ padding: 16, marginBottom: 16 }}>
      <h1 className="heading-2">Messages</h1>
        <button onClick={() => setView('compose')} className="btn-primary" data-testid="compose-new-btn">
          New Message
        </button>
      </div>

          </div>

    <div className="messages-list">
        {threads.length === 0 ? (
          <p className="body-large" style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: '48px' }}>
            No messages yet. Start a conversation!
          </p>
        ) : (
          threads.map(thread => (
            <div
              key={thread.thread_id}
              className={thread.unread_count > 0 ? 'message-item unread' : 'message-item'}
              onClick={() => handleThreadClick(thread)}
              style={{ cursor: 'pointer' }}
              data-testid={`thread-${thread.thread_id}`}
            >
              <div className="message-header">
                <span className="message-from">{thread.other_participant_name || 'User'}</span>
                <span className="message-date">{new Date(thread.latest_message?.created_at).toLocaleDateString()}</span>
              </div>
              <div className="message-subject">
                {thread.latest_message?.subject || 'No Subject'}
                {thread.unread_count > 0 && (
                  <span style={{
                    marginLeft: '8px',
                    background: '#dc2626',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '100px',
                    fontSize: '11px',
                    fontWeight: 600
                  }}>
                    {thread.unread_count} new
                  </span>
                )}
              </div>
              <div className="message-preview">
                {thread.latest_message?.message?.substring(0, 100)}
                {thread.latest_message?.message?.length > 100 ? '...' : ''}
              </div>
              <div className="body-small" style={{ color: 'var(--text-muted)', marginTop: '4px' }}>
                {thread.message_count} message{thread.message_count !== 1 ? 's' : ''}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default MessagesView;
